// TEMPORARY Vue 2 mixin for the 5 document Detail pages (quotation, invoice,
// receipt, debit note, credit note). Requires the page to expose: docTypeId,
// form, total_price / total_discount / vat_price / net_price, thisBranch,
// dataPrint, loading, list.car_type_dict. It only READS the page; the page keeps owning all data.
//
// Page hook: call `this.customerDisplayMarkSaved()` after a successful save
// (after getDataPrint). The mixin does the rest.

import { buildView, createPaymentQrGate } from '@shared/customer-display/documentView';
import { extractDisplayInput } from './documentAdapter';
import { fetchEvaluationSession, isEvaluationSessionEnabled, workstationId } from './evaluationSession';
import { DOCUMENT_VIEW_OWNER, enterDocumentPage, getPublisher, leaveDocumentPage } from './session';

const TYPING_DEBOUNCE_MS = 60;
// One check, one minute after arriving on the page: if a second screen is
// present and the cashier still hasn't opened the display, nudge them once.
const REMINDER_DELAY_MS = 60 * 1000;

// Explain how to re-enable window placement once per page load, not per click.
let deniedHintShown = false;

export default {
  data: () => ({
    customerDisplayOnline: false,
    customerDisplayShowReminder: false,
    customerDisplayDocumentId: ''
  }),

  computed: {
    customerDisplayInput() {
      return extractDisplayInput({
        docTypeId: this.docTypeId,
        documentId: this.customerDisplayDocumentId,
        form: this.form,
        totals: {
          total_price: this.total_price,
          total_discount: this.total_discount,
          vat_price: this.vat_price,
          net_price: this.net_price
        },
        branch: this.thisBranch,
        printData: this.dataPrint,
        carTypeList: this.list && this.list.car_type_dict
      });
    }
  },

  watch: {
    customerDisplayInput() {
      this.customerDisplayScheduleSync();
    },
    'thisBranch.setting.service_rating_mode'(mode) {
      if (!this.customerDisplayHydrated) return;
      const documentId = String(this.customerDisplayDocumentId || (this.form && this.form._id) || '').trim();
      if (!documentId) return;
      if (mode === 'customer_display') return this.customerDisplayEnsureEvaluationSession();
      this.customerDisplayRatingSkippedForDocumentId = documentId;
      getPublisher().setRatingSession(documentId, null);
    },
    loading(isLoading, wasLoading) {
      // The first loading -> ready transition is the initial hydration. Any
      // later toggle (save in progress, cancelled confirm) says nothing about
      // what has been saved, so it must not mark the form as saved.
      if (!wasLoading || isLoading) return;
      if (!this.customerDisplayHydrated) {
        this.customerDisplayHydrated = true;
        if (this.form && this.form._id) this.customerDisplayMarkSaved();
      }
      // A slow save may have consumed its debounced input sync while loading.
      // Republish when ready without treating a failed save as successful.
      this.customerDisplaySync();
    },
    $route(to, from) {
      // Vue Router reuses Detail.vue when navigating from an existing
      // quotation to the create route. Claim the display here as well because
      // created() is not called again for that in-place navigation.
      if (!from || to.name !== from.name || (to.params && to.params.id) !== (from.params && from.params.id)) {
        clearTimeout(this.customerDisplayTimer);
        this.customerDisplayDocumentId = '';
        this.customerDisplayHydrated = false;
        this.customerDisplayGate.reset();
        this.customerDisplayClaim(to);
      }
    }
  },

  created() {
    // Deliberately not reactive: the gate is derived state, republished
    // explicitly whenever it changes.
    this.customerDisplayGate = createPaymentQrGate();
    this.customerDisplayHydrated = false;
    this.customerDisplayTimer = null;
    this.customerDisplayRatingSkippedForDocumentId = '';
    enterDocumentPage();
    // Claim the display immediately, independent of `loading`/hydration and
    // before any license plate input: this is what clears a previous
    // customer's leftover ACTIVE/THANK_YOU view the instant the page opens,
    // rather than only once the whole init chain (profile/settings/
    // products/etc., not just the document fetch) finishes. The create-
    // quotation route explicitly claims ACTIVE; other routes claim IDLE.
    // Neither claim is derived from live form content, so this cannot expose
    // stale or half-loaded customer data before the hydration-gated sync.
    this.customerDisplayClaim();
    this.customerDisplayUnsubscribe = getPublisher().onDisplayStatus((online) => {
      this.customerDisplayOnline = online;
      // The cashier opened it (from this tab or another): the nudge no longer applies.
      if (online) this.customerDisplayShowReminder = false;
    });
    this.customerDisplayOnline = getPublisher().isDisplayOnline();
    // A second screen alone does not mean this device shows it to a customer -
    // this is a one-time nudge, not a recurring check, and never appears at all
    // on a single-screen device or a browser without screen.isExtended.
    this.customerDisplayReminderTimer = setTimeout(() => {
      if (!getPublisher().isDisplayOnline() && window.screen && window.screen.isExtended) {
        this.customerDisplayShowReminder = true;
      }
    }, REMINDER_DELAY_MS);
  },

  beforeDestroy() {
    clearTimeout(this.customerDisplayTimer);
    clearTimeout(this.customerDisplayReminderTimer);
    this.customerDisplayUnsubscribe();
    leaveDocumentPage(this.customerDisplayInput.branch);
  },

  methods: {
    // The current form matches what the server now holds: allow the QR for
    // exactly this amount/config until the next payment-affecting edit.
    customerDisplayMarkSaved(documentId) {
      const savedDocumentId = String(documentId || (this.form && this.form._id) || '').trim();
      if (savedDocumentId) this.customerDisplayDocumentId = savedDocumentId;
      const input = this.customerDisplayInput;
      this.customerDisplayGate.markSaved({
        items: input.doc.items,
        totals: input.doc.totals,
        snapshot: input.snapshot,
        legacyImageUrl: input.legacyImageUrl,
        phone: input.doc.phone
      });
      if (savedDocumentId && !isEvaluationSessionEnabled(this.thisBranch)) {
        // Clear a cached inline rating before publishing the saved view.
        this.customerDisplayRatingSkippedForDocumentId = savedDocumentId;
        getPublisher().setRatingSession(savedDocumentId, null);
      }
      this.customerDisplaySync();
      this.customerDisplayEnsureEvaluationSession();
    },

    // add_document needs the same persistent workstation id that the fallback
    // get_or_create request uses; otherwise the backend-created session would
    // have no counter identity and could not close this workstation's previous
    // waiting session.
    customerDisplayWorkstationId() {
      return workstationId();
    },

    async customerDisplayEnsureEvaluationSession({ force = false } = {}) {
      if (this.docTypeId !== 0 && this.docTypeId !== 1) return null;
      const documentId = String(this.customerDisplayDocumentId || (this.form && this.form._id) || '').trim();
      if (!documentId) return null;
      const publisher = getPublisher();
      if (!isEvaluationSessionEnabled(this.thisBranch)) {
        // Discard a session cached before this branch was switched off.
        if (this.customerDisplayRatingSkippedForDocumentId !== documentId) {
          this.customerDisplayRatingSkippedForDocumentId = documentId;
          publisher.setRatingSession(documentId, null);
        }
        return null;
      }
      const resumedAfterOff = this.customerDisplayRatingSkippedForDocumentId === documentId;
      this.customerDisplayRatingSkippedForDocumentId = '';
      if (!force && !resumedAfterOff && publisher.hasRatingSessionState(documentId)) return null;
      try {
        const session = await fetchEvaluationSession(this, documentId);
        if (!isEvaluationSessionEnabled(this.thisBranch)) {
          this.customerDisplayRatingSkippedForDocumentId = documentId;
          publisher.setRatingSession(documentId, null);
          return null;
        }
        publisher.setRatingSession(documentId, session);
        return session;
      } catch (error) {
        // Service Rating is optional: a failed lookup must not break document
        // save/display behavior. Preserve the off-to-on retry marker because
        // clearing a stale session above also marked its cache slot resolved.
        if (resumedAfterOff) this.customerDisplayRatingSkippedForDocumentId = documentId;
        return null;
      }
    },

    customerDisplayScheduleSync() {
      clearTimeout(this.customerDisplayTimer);
      this.customerDisplayTimer = setTimeout(() => this.customerDisplaySync(), TYPING_DEBOUNCE_MS);
    },

    customerDisplaySync() {
      // Also gate later route hydrations on reused component instances. The
      // original one-shot latch alone would expose the next form mid-load.
      if (this.loading || this.formHydrationFailed) return;
      // Fallback hydration: the normal trigger is the `loading` watcher's
      // first true -> false transition (above). That is a ONE-SHOT latch --
      // if something ever stops it from firing for this exact instance (e.g.
      // a Hot Module Replacement that re-renders this component without
      // re-running created()/the initial load, so loading never retoggles
      // for it), customerDisplayHydrated would otherwise stay false forever
      // and every future keystroke would silently stop reaching the Customer
      // Display -- an unrecoverable freeze for the rest of the tab's life.
      // Safe to hydrate here once loading has already settled: by
      // definition nothing is still being fetched into `form` at that point.
      if (!this.customerDisplayHydrated && !this.loading) this.customerDisplayHydrated = true;
      // Nothing is published while the page is still loading: half-hydrated
      // form data must never reach the customer.
      if (!this.customerDisplayHydrated) return;
      clearTimeout(this.customerDisplayTimer);
      this.customerDisplayPublish();
    },

    customerDisplayIsNewQuotation(route) {
      const currentRoute = route || this.$route;
      return Boolean(
        this.docTypeId === 0 &&
        currentRoute &&
        currentRoute.name === 'document-quotation-create'
      );
    },

    // Called once from created(): claims the display the instant the page
    // opens, clearing whatever the previous customer's page left showing.
    // The quotation-create route must open ActiveScreen immediately, even
    // though its form is still blank. Other document routes keep the existing
    // IDLE claim. Neither branch carries customer data before hydration.
    customerDisplayClaim(route) {
      const forceActive = this.customerDisplayIsNewQuotation(route);
      const doc = forceActive ? { docType: this.customerDisplayInput.doc.docType, status: 'awaiting-save' } : {};
      getPublisher().setView(
        buildView({
          branch: this.customerDisplayInput.branch,
          doc,
          saved: false,
          payment: null,
          phoneSaved: false,
          forceActive
        }),
        DOCUMENT_VIEW_OWNER,
        { documentId: '' }
      );
    },

    // The actual build-and-send step, shared by the hydration-gated sync
    // above and used once a real document is being edited.
    customerDisplayPublish() {
      const input = this.customerDisplayInput;
      const payment = this.customerDisplayGate.resolve({
        items: input.doc.items,
        totals: input.doc.totals,
        enabled: input.qrEnabled
      });
      getPublisher().setView(
        buildView({
          branch: input.branch,
          doc: input.doc,
          saved: this.customerDisplayGate.isInSync({ items: input.doc.items, totals: input.doc.totals }),
          payment,
          phoneSaved: this.customerDisplayGate.isPhoneSaved(input.doc.phone),
          forceActive: this.customerDisplayIsNewQuotation()
        }),
        DOCUMENT_VIEW_OWNER,
        { documentId: this.customerDisplayDocumentId || (this.form && this.form._id) || '' }
      );
    },

    // Wired to the header button: closes the display when it is open, otherwise
    // opens it (the open path must run inside the click gesture).
    toggleCustomerDisplay() {
      if (getPublisher().isDisplayOnline()) {
        getPublisher().closeDisplay();
        return Promise.resolve('closed');
      }
      return this.openCustomerDisplay();
    },

    async openCustomerDisplay() {
      const publisher = getPublisher();
      const placement = await publisher.screenPlacementState();
      // Shown at the same time as Chrome's own permission prompt (not awaited),
      // so openDisplay() still runs inside the original click gesture. It has no
      // button: it closes itself as soon as the user answers Chrome's prompt.
      const hintShown = placement === 'prompt';
      if (hintShown) {
        this.$swal({
          icon: 'info',
          title: 'กรุณากดปุ่มอนุญาต (Allow)',
          text: 'เพื่อเปิดหน้าต่างในหน้าจอลูกค้าอัตโนมัติ',
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false
        });
      }
      let result;
      try {
        result = await publisher.openDisplay();
      } finally {
        if (hintShown) this.$swal.close();
      }
      if (placement === 'denied' && result === 'opened' && !deniedHintShown) {
        deniedHintShown = true;
        this.$swal({
          icon: 'info',
          title: 'เปิดจอลูกค้าในหน้าจอนี้',
          text:
            'เบราว์เซอร์ยังไม่อนุญาตให้ย้ายหน้าต่างไปจอที่สองอัตโนมัติ ' +
            'กดไอคอนข้าง URL > การตั้งค่าเว็บไซต์ > การจัดการหน้าต่าง > อนุญาต',
          confirmButtonText: 'ตกลง'
        });
      }
      if (result === 'blocked') {
        this.$alertServerError({
          title: 'เบราว์เซอร์บล็อกหน้าต่างจอลูกค้า',
          text: 'กรุณาอนุญาต Pop-up สำหรับเว็บนี้ แล้วกดปุ่มอีกครั้ง'
        });
      }
      // Give the display a moment to announce itself, then push our state.
      this.customerDisplaySync();
      return result;
    }
  }
};
